import { createContext } from '@hover/blueprint';
import React, { useEffect, useState } from 'react';
import type { PropsWithChildren } from 'react';

import type {
  AiDisplayImageFragment,
  AiGeneratedImageFragment,
} from 'src/shared/graphql/generated';
import { useMemoContextValue } from 'src/shared/hooks';
import { logError } from 'src/shared/telemetry/logger';

import type {
  SampleDisplayImage,
  SampleGeneratedImage,
} from '../components/Home/SampleImages/sampleImages';
import { useHomeownerAppContext } from '../hooks/useHomeownerAppContext';
import { isAiDisplay, isAiGenerated, isSample } from '../types';

import { HomeownerAppMenuNames } from './HomeownerAppProvider';

interface SaveToProjectContextValue {
  displayImageToSave?: AiDisplayImageFragment;
  generatedImageToSave?: AiGeneratedImageFragment;
  setImageToSave: (
    image?: AiDisplayImageFragment | AiGeneratedImageFragment,
  ) => void;
}

const [Provider, useContext] = createContext<
  SaveToProjectContextValue | undefined
>({
  strict: false,
});

export const useSaveToProjectContext = () =>
  useContext() ?? {
    setImageToSave: () => {
      logError(
        new Error(
          'Attempted to save to project in a component not wrapped in SaveToProjectProvider.',
        ),
      );
    },
  };

interface SaveToProjectProviderProps extends PropsWithChildren<{}> {}

export const SaveToProjectProvider = ({
  children,
}: SaveToProjectProviderProps) => {
  const { setCurrentMenu } = useHomeownerAppContext();
  const [displayImageToSave, setDisplayImageToSave] = useState<
    AiDisplayImageFragment | undefined
  >();
  const [generatedImageToSave, setGeneratedImageToSave] = useState<
    AiGeneratedImageFragment | undefined
  >();

  useEffect(() => {
    setCurrentMenu(
      displayImageToSave || generatedImageToSave
        ? HomeownerAppMenuNames.ADD_TO_PROJECT
        : null,
    );
  }, [displayImageToSave, generatedImageToSave, setCurrentMenu]);

  /**
   * Sets the displayImageToSave and generatedImageToSave states based on the image type it is passed.
   * - If the image is a sample image, the user is prompted to upload their own image
   * - If the image is an AI generated image, only the generatedImageToSave state is set (this is useful
   *   when there is no display image to reference (as is the case with the edit flow where a bunch of generated
   *   images are shown for the user to choose from, and the one that is chosen)
   * - If the image is an AI display image, both the displayImageToSave and generatedImageToSave states are set
   * - If the image is not included, the current image state is cleared and the "Save to project" menu is closed
   *   as a result of that state change.
   *
   * @param image - the image the user is attempting to save
   */
  const setImageToSave = (
    image?:
      | AiDisplayImageFragment
      | AiGeneratedImageFragment
      | SampleDisplayImage
      | SampleGeneratedImage,
  ) => {
    if (isSample(image)) {
      setCurrentMenu(HomeownerAppMenuNames.UPLOAD_PHOTO);
    } else if (isAiGenerated(image)) {
      setGeneratedImageToSave(image);
      setDisplayImageToSave(undefined);
    } else if (isAiDisplay(image)) {
      setDisplayImageToSave(image);
      setGeneratedImageToSave(image.aiGeneratedImage);
    } else {
      setDisplayImageToSave(undefined);
      setGeneratedImageToSave(undefined);
    }
  };

  return (
    <Provider
      value={useMemoContextValue({
        displayImageToSave,
        generatedImageToSave,
        setImageToSave,
      })}
    >
      {children}
    </Provider>
  );
};
